import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Input, FormSection } from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import { useCreateCustomer, type CustomerPayload } from '../master-data/useMasterData';

/* ── Porte de `erpone-cadastro-de-novo-cliente` ───────────────────────────
   Informações básicas (com Nome Fantasia), endereços de cobrança e entrega,
   fiscal (IE/IM) e limite de crédito — tudo persistido via POST /customers.
   Sem auto-fill de CNPJ (sem provedor externo) e sem "cliente também é
   fornecedor" (sem flag no backend): campos manuais. */

interface AddressForm {
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

const emptyAddress = (): AddressForm => ({
  zipCode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
});

function documentTypeOf(value: string): 'CPF' | 'CNPJ' {
  return value.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ';
}

type CreatePayload = CustomerPayload & {
  stateRegistration?: string;
  municipalRegistration?: string;
  creditLimit?: number;
  billingAddress?: Record<string, string>;
  deliveryAddress?: Record<string, string>;
};

function toPayloadAddress(a: AddressForm): Record<string, string> | undefined {
  if (!a.street.trim() || !a.neighborhood.trim() || !a.city.trim() || !a.state.trim() || !a.zipCode.trim()) {
    return undefined;
  }
  const out: Record<string, string> = {
    street: a.street.trim(),
    neighborhood: a.neighborhood.trim(),
    city: a.city.trim(),
    state: a.state.trim(),
    zipCode: a.zipCode.trim(),
  };
  if (a.number.trim()) out['number'] = a.number.trim();
  if (a.complement.trim()) out['complement'] = a.complement.trim();
  return out;
}

function AddressFields({
  title,
  value,
  onChange,
}: {
  title?: string;
  value: AddressForm;
  onChange: (next: AddressForm) => void;
}): JSX.Element {
  const set = (key: keyof AddressForm) => (e: { target: { value: string } }) =>
    onChange({ ...value, [key]: e.target.value });
  const body = (
    <>
      <div className="form-row">
        <Input label="CEP" value={value.zipCode} onChange={set('zipCode')} placeholder="00000-000" />
        <Input label="Logradouro" value={value.street} onChange={set('street')} placeholder="Rua / Avenida" />
      </div>
      <div className="form-row">
        <Input label="Número" value={value.number} onChange={set('number')} placeholder="123" />
        <Input label="Complemento" value={value.complement} onChange={set('complement')} placeholder="Sala, bloco…" />
        <Input label="Bairro" value={value.neighborhood} onChange={set('neighborhood')} />
      </div>
      <div className="form-row">
        <Input label="Cidade" value={value.city} onChange={set('city')} />
        <Input label="UF" value={value.state} onChange={(e) => onChange({ ...value, state: e.target.value.toUpperCase().slice(0, 2) })} placeholder="SP" />
      </div>
    </>
  );
  if (!title) return body;
  return <FormSection legend={title}>{body}</FormSection>;
}

export function CustomerFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const createMutation = useCreateCustomer();
  const [form, setForm] = useState<CustomerPayload>({
    name: '',
    documentType: 'CNPJ',
    documentNumber: '',
    email: '',
    phone: '',
  });
  const [legalName, setLegalName] = useState('');
  const [stateRegistration, setStateRegistration] = useState('');
  const [municipalRegistration, setMunicipalRegistration] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [billing, setBilling] = useState<AddressForm>(emptyAddress());
  const [delivery, setDelivery] = useState<AddressForm>(emptyAddress());
  const [sameAddress, setSameAddress] = useState(true);

  const handleSave = (): void => {
    if (!form.name.trim()) {
      addToast('O nome é obrigatório', 'warning');
      return;
    }
    if (!form.documentNumber.trim()) {
      addToast('O documento é obrigatório', 'warning');
      return;
    }
    const limit = Number(creditLimit);
    const payload: CreatePayload = {
      ...form,
      ...(legalName.trim() ? { legalName: legalName.trim() } : {}),
      ...(stateRegistration.trim() ? { stateRegistration: stateRegistration.trim() } : {}),
      ...(municipalRegistration.trim() ? { municipalRegistration: municipalRegistration.trim() } : {}),
      ...(creditLimit.trim() && Number.isFinite(limit) && limit >= 0 ? { creditLimit: limit } : {}),
    };
    const billingPayload = toPayloadAddress(billing);
    if (billingPayload) payload['billingAddress'] = billingPayload;
    const deliveryPayload = toPayloadAddress(sameAddress ? billing : delivery);
    if (deliveryPayload) payload['deliveryAddress'] = deliveryPayload;
    createMutation.mutate(payload as CustomerPayload, {
      onSuccess: (created) => {
        addToast('Cliente criado com sucesso', 'success');
        void navigate({ to: `/crm/customers/${created.id}` });
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao criar cliente', 'error');
      },
    });
  };

  return (
    <div className="p-xl">
      <div className="mb-lg flex flex-col gap-md md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="m-0 font-display-lg text-display-lg text-on-surface">Novo Cliente</h1>
          <p className="mt-xs font-body-md text-body-md text-on-surface-variant">
            Dados comerciais, fiscais e financeiros do cliente.
          </p>
        </div>
        <div className="flex gap-sm">
          <Button variant="ghost" onClick={() => void navigate({ to: '/master-data/customers' })}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!form.name || createMutation.isPending}
            loading={createMutation.isPending}
          >
            Salvar
          </Button>
        </div>
      </div>

      <div className="form-grid">
        <FormSection legend="Informações Básicas">
          <Input
            label="Nome *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nome completo / Razão social"
          />
          <div className="form-row">
            <Input
              label="Nome Fantasia"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="Nome fantasia"
            />
            <Input
              label="Documento *"
              value={form.documentNumber}
              onChange={(e) =>
                setForm({
                  ...form,
                  documentNumber: e.target.value,
                  documentType: documentTypeOf(e.target.value),
                })
              }
              placeholder="CPF ou CNPJ"
            />
          </div>
          <div className="form-row">
            <Input
              label="E-mail"
              type="email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="email@exemplo.com"
            />
            <Input
              label="Telefone"
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(11) 99999-9999"
            />
          </div>
          {createMutation.isError ? (
            <p className="m-0 font-body-sm text-body-sm text-error">
              {(createMutation.error as Error)?.message ?? ''}
            </p>
          ) : null}
        </FormSection>

        <AddressFields title="Endereço de Cobrança" value={billing} onChange={setBilling} />

        <FormSection legend="Endereço de Entrega">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={sameAddress}
              onChange={(e) => setSameAddress(e.target.checked)}
            />
            Mesmo endereço da cobrança
          </label>
          {!sameAddress && (
            <AddressFields value={delivery} onChange={setDelivery} />
          )}
        </FormSection>

        <FormSection legend="Fiscal e Financeiro">
          <div className="form-row">
            <Input
              label="Inscrição Estadual (IE)"
              value={stateRegistration}
              onChange={(e) => setStateRegistration(e.target.value)}
              placeholder="Exigida p/ contribuinte normal"
            />
            <Input
              label="Inscrição Municipal (IM)"
              value={municipalRegistration}
              onChange={(e) => setMunicipalRegistration(e.target.value)}
            />
            <Input
              label="Limite de Crédito (R$)"
              type="number"
              min="0"
              step="0.01"
              value={creditLimit}
              onChange={(e) => setCreditLimit(e.target.value)}
              placeholder="0,00"
            />
          </div>
        </FormSection>
      </div>

      <style>{`
        .form-grid { display: flex; flex-direction: column; gap: 1rem; max-width: 1040px; }
        .form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; }
        .toggle-label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #191c1e; cursor: pointer; }
        .toggle-label input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
      `}</style>
    </div>
  );
}
